import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Home, LogOut, AlertTriangle, Users, Search, 
  ChevronDown, ChevronUp, RefreshCw, Eye, Clock, Activity,
  Fingerprint, Globe, UserX, CheckCircle, Trash2, X, Radio,
  ShoppingCart, TrendingUp, Gavel, DollarSign, User
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useActivityTracker } from '../hooks/useActivityTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

// Format date to readable EST
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format page path to readable name - ALL UPPERCASE
const formatPage = (page, productName) => {
  if (!page) return 'UNKNOWN';
  
  if (page.startsWith('/product/')) {
    if (productName) return productName.toUpperCase();
    return 'VIEWING PRODUCT';
  }
  
  const pageMap = {
    '/': 'HOME',
    '/catalog': 'CATALOG',
    '/cart': 'CART',
    '/checkout': 'CHECKOUT',
    '/login': 'LOGIN',
    '/register': 'REGISTER',
    '/dashboard': 'MY ACCOUNT',
    '/admin': 'ADMIN',
  };
  
  if (pageMap[page]) return pageMap[page];
  if (page.startsWith('/admin')) return 'ADMIN';
  return page.replace(/^\//, '').toUpperCase() || 'HOME';
};

// Shared IP Card Component
function SharedIPCard({ ipData, onDelete, onViewDetails }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="border border-zinc-800 bg-zinc-900/50 p-3 sm:p-4 space-y-2 sm:space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-500 flex-shrink-0" />
          <span className="font-mono text-xs sm:text-sm text-white break-all">{ipData.ip_address}</span>
          <Badge 
            className={`${
              ipData.risk_level === 'HIGH' 
                ? 'bg-red-500/20 text-red-400 border-red-500/50' 
                : 'bg-amber-500/20 text-amber-400 border-amber-500/50'
            } font-mono text-[10px] sm:text-xs`}
          >
            {ipData.risk_level}
          </Badge>
        </div>
        <div className="flex items-center gap-2 justify-between sm:justify-end">
          <span className="font-mono text-[10px] sm:text-xs text-zinc-500">
            {ipData.user_count} USERS
          </span>
          <button 
            onClick={() => onDelete(ipData.ip_address)}
            className="p-1 text-red-400 hover:text-red-300 transition-colors"
            title="Delete this IP record"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-[10px] sm:text-xs text-zinc-500 font-mono">
        <span>FIRST: {formatDate(ipData.first_seen)}</span>
        <span>LAST: {formatDate(ipData.last_seen)}</span>
        <span>ACCESSES: {ipData.total_accesses || 0}</span>
      </div>
      
      {expanded && (
        <div className="mt-3 sm:mt-4 space-y-2 border-t border-zinc-800 pt-3 sm:pt-4">
          <p className="font-mono text-[10px] sm:text-xs text-zinc-400 mb-2">USERS SHARING THIS IP:</p>
          {ipData.users?.map((user, idx) => (
            <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-zinc-800/50 p-2 sm:p-3 border border-zinc-700">
              <div className="min-w-0">
                <p className="font-mono text-xs sm:text-sm text-white truncate">{user.full_name?.toUpperCase() || 'UNKNOWN'}</p>
                <p className="font-mono text-[10px] sm:text-xs text-zinc-500 truncate">{user.email}</p>
              </div>
              <div className="text-left sm:text-right flex-shrink-0">
                <p className="font-mono text-[10px] sm:text-xs text-zinc-400">
                  {user.access_count || 0} ACCESSES
                </p>
                <p className="font-mono text-[10px] text-zinc-600">
                  LAST: {formatDate(user.last_seen)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// User Search Result Component
function UserIPHistory({ userData }) {
  if (!userData) return null;
  
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="border border-zinc-800 bg-zinc-900/50 p-3 sm:p-4">
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <Users className="w-4 h-4 sm:w-5 sm:h-5 text-tiffany-500 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-mono text-sm sm:text-base text-white truncate">{userData.full_name?.toUpperCase() || 'UNKNOWN'}</p>
            <p className="font-mono text-[10px] sm:text-xs text-zinc-500 truncate">{userData.email}</p>
          </div>
        </div>
        
        <div className="flex gap-3 sm:gap-4 text-[10px] sm:text-xs font-mono">
          <span className="text-zinc-400">
            IPS: <span className="text-white">{userData.total_ips}</span>
          </span>
          <span className="text-zinc-400">
            SHARED: <span className={userData.shared_ips_count > 0 ? 'text-amber-400' : 'text-green-400'}>
              {userData.shared_ips_count}
            </span>
          </span>
        </div>
      </div>
      
      {userData.ip_addresses?.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] sm:text-xs text-zinc-400">IP ADDRESSES:</p>
          {userData.ip_addresses.map((ip, idx) => (
            <div 
              key={idx} 
              className={`border ${ip.is_shared ? 'border-amber-500/50 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900/50'} p-2 sm:p-3`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500 flex-shrink-0" />
                  <span className="font-mono text-xs sm:text-sm text-white truncate">{ip.ip_address}</span>
                  {ip.is_shared && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 font-mono text-[10px] flex-shrink-0">
                      SHARED
                    </Badge>
                  )}
                </div>
                <span className="font-mono text-[10px] sm:text-xs text-zinc-500 pl-5 sm:pl-0">
                  {ip.access_count || 0} ACCESSES
                </span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 mt-2 text-[10px] font-mono text-zinc-600 pl-5 sm:pl-0">
                <span>FIRST: {formatDate(ip.first_seen)}</span>
                <span>LAST: {formatDate(ip.last_seen)}</span>
              </div>
              
              {ip.is_shared && ip.shared_with?.length > 0 && (
                <div className="mt-2 sm:mt-3 pl-5 sm:pl-4 border-l-2 border-amber-500/30">
                  <p className="font-mono text-[10px] text-amber-400 mb-1">ALSO USED BY:</p>
                  {ip.shared_with.map((other, oidx) => (
                    <p key={oidx} className="font-mono text-[10px] sm:text-xs text-zinc-400 truncate">
                      {other.full_name} ({other.email})
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// IP Lookup Result Component  
function IPLookupResult({ ipData }) {
  if (!ipData) return null;
  
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="border border-zinc-800 bg-zinc-900/50 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-3 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-tiffany-500 flex-shrink-0" />
            <span className="font-mono text-sm sm:text-base text-white truncate">{ipData.ip_address}</span>
          </div>
          <Badge 
            className={`${
              ipData.risk_level === 'HIGH' 
                ? 'bg-red-500/20 text-red-400 border-red-500/50' 
                : ipData.risk_level === 'MEDIUM'
                ? 'bg-amber-500/20 text-amber-400 border-amber-500/50'
                : 'bg-green-500/20 text-green-400 border-green-500/50'
            } font-mono text-[10px] sm:text-xs w-fit`}
          >
            {ipData.risk_level}
          </Badge>
        </div>
        
        <p className="font-mono text-[10px] sm:text-xs text-zinc-400">
          {ipData.user_count} USER(S) HAVE USED THIS IP
        </p>
      </div>
      
      {ipData.users?.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[10px] sm:text-xs text-zinc-400">USERS:</p>
          {ipData.users.map((user, idx) => (
            <div key={idx} className="border border-zinc-800 bg-zinc-900/50 p-2 sm:p-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-xs sm:text-sm text-white truncate">{user.full_name?.toUpperCase() || 'UNKNOWN'}</p>
                  <p className="font-mono text-[10px] sm:text-xs text-zinc-500 truncate">{user.email}</p>
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <p className="font-mono text-[10px] sm:text-xs text-zinc-400">{user.access_count || 0} ACCESSES</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 mt-2 text-[10px] font-mono text-zinc-600">
                <span>FIRST: {formatDate(user.first_seen)}</span>
                <span>LAST: {formatDate(user.last_seen)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminIPIntelligence() {
  const { user, logout, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sharedIPs, setSharedIPs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Live monitor state
  const [liveData, setLiveData] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [ipIntel, setIpIntel] = useState([]);
  const [suspiciousIps, setSuspiciousIps] = useState({ suspicious_ips: [], counts: {} });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedActivityUser, setSelectedActivityUser] = useState(null);
  const [selectedActivityIP, setSelectedActivityIP] = useState(null);
  
  // Activity tracker only runs when page is open
  useActivityTracker();
  
  // Search states
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResult, setUserSearchResult] = useState(null);
  const [userSearching, setUserSearching] = useState(false);
  
  const [ipSearchQuery, setIPSearchQuery] = useState('');
  const [ipSearchResult, setIPSearchResult] = useState(null);
  const [ipSearching, setIPSearching] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const getAuthHeader = useCallback(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchData = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const headers = getAuthHeader();
      
      const [statsRes, sharedRes, recentRes, liveRes, activityRes, ipIntelRes, suspiciousRes] = await Promise.all([
        axios.get(`${API}/admin/ip-intelligence/stats`, { headers }),
        axios.get(`${API}/admin/ip-intelligence/shared-ips`, { headers }),
        axios.get(`${API}/admin/ip-intelligence/recent?limit=20`, { headers }),
        axios.get(`${API}/admin/live-monitor`, { headers }),
        axios.get(`${API}/admin/activity-log`, { headers }).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API}/admin/activity-log/ip-intel`, { headers }).catch(() => ({ data: { ip_matches: [] } })),
        axios.get(`${API}/admin/suspicious-ips`, { headers }).catch(() => ({ data: { suspicious_ips: [], counts: {} } }))
      ]);
      
      setStats(statsRes.data);
      setSharedIPs(sharedRes.data.shared_ips || []);
      setRecentActivity(recentRes.data.recent_activity || []);
      setLiveData(liveRes.data);
      setActivityLog(activityRes.data.activities || []);
      setIpIntel(ipIntelRes.data.ip_matches || []);
      setSuspiciousIps(suspiciousRes.data || { suspicious_ips: [], counts: {} });
    } catch (error) {
      console.error('Failed to fetch security data:', error);
      if (showLoading) toast.error('FAILED TO LOAD SECURITY DATA');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    // Small delay on initial load to let auth settle
    const initTimeout = setTimeout(() => {
      fetchData(initialLoad);
    }, 500);
    
    // Auto-refresh every 30 seconds when enabled (reduced from 10s to prevent flooding)
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => fetchData(false), 30000);
    }
    
    return () => {
      clearTimeout(initTimeout);
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchData, initialLoad]);

  // Clear activity log
  const handleClearActivityLog = async () => {
    if (!window.confirm('CLEAR ALL ACTIVITY LOG DATA? THIS CANNOT BE UNDONE.')) return;
    
    try {
      await axios.delete(`${API}/admin/activity-log/clear`, { headers: getAuthHeader() });
      setActivityLog([]);
      toast.success('ACTIVITY LOG CLEARED');
    } catch (error) {
      console.error('Failed to clear activity log:', error);
      toast.error('FAILED TO CLEAR ACTIVITY LOG');
    }
  };

  // Clear trending products
  const handleClearTrending = async () => {
    if (!window.confirm('CLEAR TRENDING PRODUCTS DATA? THIS CANNOT BE UNDONE.')) return;
    
    try {
      await axios.delete(`${API}/admin/trending/clear`, { headers: getAuthHeader() });
      // Refresh data to update the UI
      fetchData(false);
      toast.success('TRENDING PRODUCTS CLEARED');
    } catch (error) {
      console.error('Failed to clear trending:', error);
      toast.error('FAILED TO CLEAR TRENDING PRODUCTS');
    }
  };

  const searchUserByEmail = async () => {
    if (!userSearchQuery.trim()) {
      toast.error('PLEASE ENTER AN EMAIL TO SEARCH');
      return;
    }
    
    try {
      setUserSearching(true);
      setUserSearchResult(null);
      
      // First find the user by email
      const usersRes = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
      const foundUser = usersRes.data.users?.find(
        u => u.email?.toLowerCase() === userSearchQuery.toLowerCase().trim()
      );
      
      if (!foundUser) {
        toast.error('USER NOT FOUND');
        return;
      }
      
      // Then get their IP history
      const res = await axios.get(
        `${API}/admin/ip-intelligence/user/${foundUser.id}`, 
        { headers: getAuthHeader() }
      );
      setUserSearchResult(res.data);
    } catch (error) {
      console.error('User search failed:', error);
      toast.error('FAILED TO SEARCH USER');
    } finally {
      setUserSearching(false);
    }
  };

  const searchIP = async () => {
    if (!ipSearchQuery.trim()) {
      toast.error('PLEASE ENTER AN IP ADDRESS TO SEARCH');
      return;
    }
    
    try {
      setIPSearching(true);
      setIPSearchResult(null);
      
      const res = await axios.get(
        `${API}/admin/ip-intelligence/ip/${encodeURIComponent(ipSearchQuery.trim())}`,
        { headers: getAuthHeader() }
      );
      setIPSearchResult(res.data);
      
      if (res.data.user_count === 0) {
        toast.info('NO USERS FOUND FOR THIS IP');
      }
    } catch (error) {
      console.error('IP search failed:', error);
      toast.error('FAILED TO SEARCH IP');
    } finally {
      setIPSearching(false);
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-red-500 font-mono">ACCESS DENIED</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-20 sm:pt-24 pb-12 px-3 sm:px-4 md:px-8 max-w-7xl mx-auto">
        {/* Title */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-sans text-xl sm:text-3xl text-white flex items-center gap-2 sm:gap-3" data-testid="security-tools-title">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-tiffany-500" />
              SECURITY TOOLS
            </h1>
            <p className="font-mono text-xs sm:text-sm text-zinc-500 mt-1">
              LIVE MONITORING, IP TRACKING & FRAUD DETECTION
            </p>
          </div>
          <Button
            onClick={fetchData}
            disabled={loading}
            className="h-9 sm:h-10 px-3 sm:px-4 bg-zinc-800 text-white hover:bg-zinc-700 font-mono text-xs sm:text-sm rounded-none w-full sm:w-auto"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-6 sm:mb-8">
            <div className="border border-zinc-800 bg-zinc-900/50 p-3 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500" />
                <span className="font-mono text-[10px] sm:text-xs text-zinc-500">UNIQUE IPs</span>
              </div>
              <p className="font-mono text-xl sm:text-2xl text-white">{stats.total_unique_ips}</p>
            </div>
            
            <div className="border border-zinc-800 bg-zinc-900/50 p-3 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500" />
                <span className="font-mono text-[10px] sm:text-xs text-zinc-500">TRACKED</span>
              </div>
              <p className="font-mono text-xl sm:text-2xl text-white">{stats.total_users_tracked}</p>
            </div>
            
            <div className="border border-amber-500/30 bg-amber-500/5 p-3 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500" />
                <span className="font-mono text-[10px] sm:text-xs text-amber-500">FLAGGED</span>
              </div>
              <p className="font-mono text-xl sm:text-2xl text-amber-400">{stats.flagged_shared_ips}</p>
            </div>
            
            <div className="border border-zinc-800 bg-zinc-900/50 p-3 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <Activity className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-500" />
                <span className="font-mono text-[10px] sm:text-xs text-zinc-500">24H</span>
              </div>
              <p className="font-mono text-xl sm:text-2xl text-white">{stats.recent_activity_24h}</p>
            </div>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="live" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1 rounded-none grid grid-cols-5 w-full">
            <TabsTrigger 
              value="live" 
              className="font-mono text-[10px] sm:text-xs data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400 rounded-none px-1 sm:px-2"
            >
              <Radio className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">LIVE</span>
              <span className="ml-1">({liveData?.active_users?.total || 0})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="flagged" 
              className="font-mono text-[10px] sm:text-xs data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 rounded-none px-1 sm:px-2"
            >
              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">FLAGGED</span>
              <span className="ml-1">({sharedIPs.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="user-search" 
              className="font-mono text-[10px] sm:text-xs data-[state=active]:bg-tiffany-500/20 data-[state=active]:text-tiffany-500 rounded-none px-1 sm:px-2"
            >
              <Search className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">USER</span>
            </TabsTrigger>
            <TabsTrigger 
              value="ip-search" 
              className="font-mono text-[10px] sm:text-xs data-[state=active]:bg-tiffany-500/20 data-[state=active]:text-tiffany-500 rounded-none px-1 sm:px-2"
            >
              <Globe className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">IP</span>
            </TabsTrigger>
            <TabsTrigger 
              value="recent" 
              className="font-mono text-[10px] sm:text-xs data-[state=active]:bg-tiffany-500/20 data-[state=active]:text-tiffany-500 rounded-none px-1 sm:px-2"
            >
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">RECENT</span>
            </TabsTrigger>
            <TabsTrigger 
              value="suspects" 
              className="font-mono text-[10px] sm:text-xs data-[state=active]:bg-red-500/20 data-[state=active]:text-red-500 rounded-none px-1 sm:px-2"
            >
              <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">SUSPECTS</span>
            </TabsTrigger>
          </TabsList>

          {/* Live Monitor Tab */}
          <TabsContent value="live" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-zinc-600'}`} />
                <span className="font-mono text-xs text-zinc-400">
                  {autoRefresh ? 'AUTO-REFRESHING (30s)' : 'PAUSED'}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`h-8 px-3 rounded-none font-mono text-xs ${autoRefresh ? 'border-green-500/50 text-green-400' : 'border-zinc-700 text-zinc-500'}`}
              >
                {autoRefresh ? 'PAUSE' : 'RESUME'}
              </Button>
            </div>
            
            {/* Active Users */}
            <div className="border border-green-500/30 bg-green-500/5 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-mono text-sm text-green-400 flex items-center gap-2">
                  <Radio className="w-4 h-4" />
                  ACTIVE ON SITE ({liveData?.active_users?.total || 0})
                </h3>
              </div>
              
              {liveData?.active_users?.users?.length > 0 ? (
                <div className="space-y-2">
                  {liveData.active_users.users.map((activeUser, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-900/50 p-3 border border-zinc-800">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-mono text-sm text-white truncate">{activeUser.full_name?.toUpperCase() || activeUser.email || 'GUEST'}</p>
                          <p className="font-mono text-[10px] text-tiffany-400 truncate">{formatPage(activeUser.current_page, activeUser.viewing_product)}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="font-mono text-[10px] text-zinc-400">{activeUser.ip_address}</p>
                        <p className="font-mono text-[10px] text-zinc-600">
                          {new Date(activeUser.last_seen).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
                  <p className="font-mono text-sm text-zinc-500">NO ACTIVE USERS RIGHT NOW</p>
                </div>
              )}
            </div>
            
            {/* Trending Products - What users are looking at */}
            {liveData?.trending_products?.length > 0 && (
              <div className="border border-tiffany-500/30 bg-tiffany-500/5 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-mono text-sm text-tiffany-400 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    TRENDING PRODUCTS (24H)
                  </h3>
                  <Button
                    onClick={handleClearTrending}
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    CLEAR
                  </Button>
                </div>
                <div className="space-y-2">
                  {liveData.trending_products.map((product, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-zinc-900/50 p-2 border border-zinc-800 gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="font-mono text-xs text-zinc-500 flex-shrink-0">#{idx + 1}</span>
                        <span className="font-mono text-xs sm:text-sm text-white truncate">{product.product_name}</span>
                      </div>
                      <Badge className="bg-tiffany-500/20 text-tiffany-400 border-0 rounded-none text-xs flex-shrink-0">
                        {product.view_count} VIEWS
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* REAL-TIME ACTIVITY LOG - Every page change */}
            <div className="border border-green-500/30 bg-green-500/5">
              <div className="p-3 border-b border-green-500/20 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Activity className="w-4 h-4 text-green-500 animate-pulse flex-shrink-0" />
                  <span className="font-mono text-xs text-green-400">ACTIVITY LOG</span>
                  <Badge className="bg-green-500/20 text-green-400 border-0 rounded-none text-[10px]">
                    24H
                  </Badge>
                  <span className="font-mono text-[10px] text-zinc-500">{activityLog.length} EVENTS</span>
                </div>
                <div className="flex items-center gap-2">
                  {activityLog.length > 0 && (
                    <Button
                      onClick={handleClearActivityLog}
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      CLEAR
                    </Button>
                  )}
                </div>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {activityLog.length === 0 ? (
                  <div className="p-6 text-center">
                    <Activity className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
                    <p className="font-mono text-xs text-zinc-500">NO ACTIVITY LOGGED YET</p>
                    <p className="font-mono text-[10px] text-zinc-600 mt-1">PAGE VIEWS WILL APPEAR HERE IN REAL-TIME</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800/50">
                    {(() => {
                      const ipUserMap = {};
                      ipIntel.forEach(match => {
                        if (match.known_users?.length > 0) {
                          ipUserMap[match.ip_address] = match.known_users;
                        }
                      });
                      
                      return activityLog.map((log, idx) => {
                        const isAnon = !log.user_id;
                        const matched = isAnon && log.ip_address && ipUserMap[log.ip_address];
                        const displayName = log.user_name && log.user_name !== 'Anonymous' 
                          ? log.user_name.toUpperCase() 
                          : matched ? `~${(matched[0]?.name || matched[0]?.email || 'UNKNOWN').toUpperCase()}` : 'ANONYMOUS';
                        
                        return (
                          <div key={idx} className={`p-2 flex items-center gap-2 overflow-hidden ${
                            log.suspicious ? 'bg-red-500/5 border-l-2 border-l-red-500' : 'hover:bg-zinc-900/50'
                          }`}>
                            <div className="w-16 flex-shrink-0">
                              <span className="font-mono text-[10px] text-zinc-400 block">
                                {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })}
                              </span>
                              <span className="font-mono text-[9px] text-zinc-600 block">
                                {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                              </span>
                            </div>
                            <div className="flex items-start gap-1 min-w-0 w-[130px] flex-shrink-0 overflow-hidden">
                              <User className={`w-3 h-3 flex-shrink-0 mt-0.5 ${isAnon && matched ? 'text-orange-400' : isAnon ? 'text-red-400' : 'text-zinc-500'}`} />
                              <div className="min-w-0">
                                <span className={`font-mono text-[10px] block truncate ${isAnon && matched ? 'text-orange-400' : isAnon ? 'text-red-400' : 'text-white'}`}>
                                  {displayName}
                                </span>
                                <span className="font-mono text-[9px] text-zinc-600 block truncate">
                                  {log.ip_address || '—'}
                                </span>
                              </div>
                            </div>
                            <span className="text-zinc-600 flex-shrink-0">→</span>
                            <div className="min-w-0 flex-1 overflow-hidden">
                              {log.product_name ? (
                                <span className="font-mono text-[10px] text-tiffany-400 truncate block">
                                  {log.product_name.toUpperCase()}
                                </span>
                              ) : (
                                <span className="font-mono text-[10px] text-zinc-400 truncate block">
                                  {formatPage(log.page)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Flagged IPs Tab */}
          <TabsContent value="flagged" className="space-y-4">
            {loading ? (
              <div className="border border-zinc-800 p-8 text-center">
                <RefreshCw className="w-6 h-6 animate-spin text-zinc-500 mx-auto mb-2" />
                <p className="font-mono text-sm text-zinc-500">LOADING FLAGGED IPs...</p>
              </div>
            ) : sharedIPs.length === 0 ? (
              <div className="border border-green-500/30 bg-green-500/5 p-8 text-center">
                <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-3" />
                <p className="font-mono text-sm text-green-400">NO FLAGGED IPs</p>
                <p className="font-mono text-xs text-zinc-500 mt-1">
                  NO IP ADDRESSES ARE CURRENTLY SHARED BY 2+ USERS
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-xs text-amber-400">
                    {sharedIPs.length} IP ADDRESS(ES) FLAGGED FOR POTENTIAL FRAUD
                  </p>
                </div>
                {sharedIPs.map((ip, idx) => (
                  <SharedIPCard 
                    key={idx} 
                    ipData={ip} 
                    onDelete={async (ipAddress) => {
                      if (window.confirm(`DELETE ALL RECORDS FOR IP ${ipAddress}?`)) {
                        try {
                          await axios.delete(`${API}/admin/ip-intelligence/flagged/${encodeURIComponent(ipAddress)}`, { headers: getAuthHeader() });
                          toast.success('IP RECORDS DELETED');
                          fetchData();
                        } catch (error) {
                          toast.error('FAILED TO DELETE IP RECORDS');
                        }
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* User Search Tab */}
          <TabsContent value="user-search" className="space-y-4">
            <div className="border border-zinc-800 bg-zinc-900/50 p-4">
              <p className="font-mono text-xs text-zinc-400 mb-3">
                SEARCH BY USER EMAIL TO SEE ALL IP ADDRESSES THEY'VE USED
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchUserByEmail()}
                  className="flex-1 h-10 bg-zinc-900 border-zinc-700 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  data-testid="user-search-input"
                />
                <Button
                  onClick={searchUserByEmail}
                  disabled={userSearching}
                  className="h-10 px-6 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-sm rounded-none"
                  data-testid="user-search-btn"
                >
                  {userSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            {userSearchResult && <UserIPHistory userData={userSearchResult} />}
          </TabsContent>

          {/* IP Search Tab */}
          <TabsContent value="ip-search" className="space-y-4">
            <div className="border border-zinc-800 bg-zinc-900/50 p-4">
              <p className="font-mono text-xs text-zinc-400 mb-3">
                SEARCH BY IP ADDRESS TO SEE ALL USERS WHO HAVE USED IT
              </p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="192.168.1.1"
                  value={ipSearchQuery}
                  onChange={(e) => setIPSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchIP()}
                  className="flex-1 h-10 bg-zinc-900 border-zinc-700 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  data-testid="ip-search-input"
                />
                <Button
                  onClick={searchIP}
                  disabled={ipSearching}
                  className="h-10 px-6 bg-tiffany-500 text-black hover:bg-tiffany-400 font-mono text-sm rounded-none"
                  data-testid="ip-search-btn"
                >
                  {ipSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            {ipSearchResult && <IPLookupResult ipData={ipSearchResult} />}
          </TabsContent>

          {/* Recent Activity Tab */}
          <TabsContent value="recent" className="space-y-4">
            {loading ? (
              <div className="border border-zinc-800 p-6 sm:p-8 text-center">
                <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-zinc-500 mx-auto mb-2" />
                <p className="font-mono text-xs sm:text-sm text-zinc-500">LOADING...</p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="border border-zinc-800 p-6 sm:p-8 text-center">
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-600 mx-auto mb-3" />
                <p className="font-mono text-xs sm:text-sm text-zinc-500">NO RECENT ACTIVITY</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] sm:text-xs text-zinc-400">
                    RECENT IP-USER MAPPINGS
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (window.confirm('CLEAR ALL IP-USER MAPPING HISTORY? THIS CANNOT BE UNDONE.')) {
                        try {
                          const res = await axios.delete(`${API}/admin/ip-intelligence/recent/clear`, { headers: getAuthHeader() });
                          toast.success(`CLEARED ${res.data.deleted} RECORDS`);
                          fetchData();
                        } catch (error) {
                          toast.error('FAILED TO CLEAR RECORDS');
                        }
                      }
                    }}
                    className="h-7 px-2 text-xs rounded-none border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> CLEAR ALL
                  </Button>
                </div>
                <div className="border border-zinc-800 divide-y divide-zinc-800">
                  {recentActivity.map((record, idx) => (
                    <div key={idx} className="p-2 sm:p-3 hover:bg-zinc-900/50">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                        <div className="flex items-center gap-2 min-w-0">
                          <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-zinc-600 flex-shrink-0" />
                          <span className="font-mono text-[10px] sm:text-xs text-zinc-400 truncate">{record.ip_address}</span>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 min-w-0">
                          <div className="min-w-0">
                            <span className="font-mono text-xs sm:text-sm text-white truncate block">{record.full_name?.toUpperCase() || 'UNKNOWN'}</span>
                            <span className="font-mono text-[10px] text-zinc-600 truncate block sm:hidden">{record.email}</span>
                          </div>
                          <span className="font-mono text-[10px] text-zinc-600 flex-shrink-0 hidden sm:block">
                            {formatDate(record.last_seen)}
                          </span>
                        </div>
                      </div>
                      <div className="sm:hidden mt-1 text-right">
                        <span className="font-mono text-[10px] text-zinc-600">
                          {formatDate(record.last_seen)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Suspects Tab */}
          <TabsContent value="suspects" className="space-y-4">
            <div className="border border-red-500/30 bg-red-500/5">
              <div className="p-3 border-b border-red-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="font-mono text-xs text-red-400">SUSPICIOUS IPS</span>
                  <span className="font-mono text-[10px] text-zinc-500">
                    {suspiciousIps.counts?.flagged || 0} FLAGGED • {suspiciousIps.counts?.banned || 0} BANNED
                  </span>
                </div>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {(!suspiciousIps.suspicious_ips || suspiciousIps.suspicious_ips.length === 0) ? (
                  <div className="p-6 text-center">
                    <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                    <p className="font-mono text-xs text-zinc-500">NO SUSPICIOUS IPS DETECTED</p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800/50">
                    {suspiciousIps.suspicious_ips.map((ip, idx) => (
                      <div key={idx} className={`p-3 ${
                        ip.status === 'banned' ? 'bg-red-500/10' : ip.status === 'cleared' ? 'bg-green-500/5 opacity-50' : ''
                      }`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="min-w-0">
                            <span className="font-mono text-sm text-white">{ip.ip_address}</span>
                            <span className={`font-mono text-[9px] ml-2 px-1.5 py-0.5 ${
                              ip.status === 'banned' ? 'bg-red-500/20 text-red-400' : 
                              ip.status === 'cleared' ? 'bg-green-500/20 text-green-400' : 
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {ip.status?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="font-mono text-[10px] text-zinc-500">{ip.hit_count} HITS</span>
                            {ip.recent_hits_1h > 0 && (
                              <span className="font-mono text-[9px] text-red-400 bg-red-500/10 px-1">{ip.recent_hits_1h} LAST HR</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex flex-wrap gap-1">
                            {(ip.flags || []).map((flag, fIdx) => (
                              <span key={fIdx} className="font-mono text-[9px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5">
                                {flag.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            ))}
                          </div>
                        </div>
                        {ip.recent_pages && ip.recent_pages.length > 0 && (
                          <p className="font-mono text-[9px] text-zinc-600 mb-2 truncate">
                            RECENT: {ip.recent_pages.join(' → ')}
                          </p>
                        )}
                        <div className="flex gap-1">
                          {ip.status !== 'banned' && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/suspicious-ips/${encodeURIComponent(ip.ip_address)}/status`, 
                                    { status: 'banned' }, { headers: getAuthHeader() });
                                  toast.success(`${ip.ip_address} BANNED`);
                                  fetchData();
                                } catch (e) { toast.error('FAILED'); }
                              }}
                              className="font-mono text-[9px] bg-red-500/20 text-red-400 px-2 py-1 hover:bg-red-500/30"
                            >
                              BAN
                            </button>
                          )}
                          {ip.status !== 'cleared' && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/suspicious-ips/${encodeURIComponent(ip.ip_address)}/status`, 
                                    { status: 'cleared' }, { headers: getAuthHeader() });
                                  toast.success(`${ip.ip_address} CLEARED`);
                                  fetchData();
                                } catch (e) { toast.error('FAILED'); }
                              }}
                              className="font-mono text-[9px] bg-green-500/20 text-green-400 px-2 py-1 hover:bg-green-500/30"
                            >
                              CLEAR
                            </button>
                          )}
                          {ip.status === 'banned' && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/suspicious-ips/${encodeURIComponent(ip.ip_address)}/status`, 
                                    { status: 'flagged' }, { headers: getAuthHeader() });
                                  toast.success(`${ip.ip_address} UNFLAGGED`);
                                  fetchData();
                                } catch (e) { toast.error('FAILED'); }
                              }}
                              className="font-mono text-[9px] bg-zinc-700 text-zinc-400 px-2 py-1 hover:bg-zinc-600"
                            >
                              UNBAN
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          </TabsContent>
        </Tabs>

        {/* Back to Admin Link */}
        <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-zinc-800">
          <Link 
            to="/admin"
            className="font-mono text-xs sm:text-sm text-zinc-500 hover:text-tiffany-500 transition-colors"
          >
            ← Back to Admin
          </Link>
        </div>
      </main>
    </div>
  );
}
